import {
    PlanningGetById,
    ProjectsListProjectGetRecordsList,
    ProjectResponseGetById,
    VariantGetById,
    ProductGetById
} from '@nx-customer-apps/api-planning-projects';
import { ProjectStatus, ProjectTag } from '@nx-customer-apps/shared/enums';

export class ProjectUtils {
    static getAllPlanningsFromVariants(project: ProjectResponseGetById, variantId?: string): PlanningGetById[] | undefined {
        const allPlanningsGroupedByVariants = project.variants
            .filter(variant => (variantId ? variant.id === variantId : true))
            .map(variant => variant.plannings)
            .filter(plannings => Boolean(plannings)) as PlanningGetById[][];

        const allPlannings = allPlanningsGroupedByVariants.reduce((acc, cur) => {
            acc?.push(...cur);
            return acc;
        }, []);

        return allPlannings;
    }

    static getPlanningsWithProductsFromVariants(project: ProjectResponseGetById, variantId?: string): PlanningGetById[] | undefined {
        const allPlannings = ProjectUtils.getAllPlanningsFromVariants(project, variantId);
        return allPlannings?.filter(planning => planning?.products?.length);
    }

    static getSelectedVariant(project: ProjectResponseGetById): VariantGetById | undefined {
        return project?.variants?.find(variant => variant.isSelected);
    }

    static getAllPlanningsFromAllVariantsByAppId(
        project: ProjectResponseGetById,
        applicationId: PlanningGetById['applicationId']
    ): (PlanningGetById | undefined)[] {
        const allPlanningFromAllVariants = project.variants
            .map(variant => variant.plannings)
            .filter(plannings => plannings?.length)
            .flat(1);

        const variantsByAppId = allPlanningFromAllVariants.filter(planning => planning?.applicationId === applicationId);

        return variantsByAppId;
    }

    static getPlanningsFromSelectedVariant(
        project: ProjectResponseGetById,
        options?: { withEmptyProducts: boolean }
    ): PlanningGetById[] | undefined {
        const selectedVariant = ProjectUtils.getSelectedVariant(project);
        return selectedVariant?.plannings?.filter(planning => planning?.products?.length || options?.withEmptyProducts);
    }

    static hasPlanningsInProgress(project: ProjectResponseGetById, options?: { variantId: string }): boolean {
        const allPlannings = ProjectUtils.getAllPlanningsFromVariants(project, options?.variantId);
        return Boolean(allPlannings?.some(planning => !planning.products?.length));
    }

    static hasPlanningAProductOfType(
        project: ProjectResponseGetById,
        options: { variantId: string; applicationId: string; productType: ProductGetById['productType'] | ProductGetById['productType'][] }
    ): boolean {
        const planning = ProjectUtils.getPlanningFromVariantByApplicationId(project, options);
        if (Array.isArray(options.productType)) {
            return Boolean(planning?.products?.some(product => options.productType.includes(product.productType)));
        }
        return Boolean(planning?.products?.some(product => product.productType === options.productType));
    }

    static getPlanningFromVariantByApplicationId(
        project: ProjectResponseGetById,
        options: { applicationId: string; variantId: string }
    ): PlanningGetById | undefined {
        const plannings = ProjectUtils.getAllPlanningsFromVariants(project, options.variantId);
        return plannings?.find(planning => planning.applicationId === options.applicationId);
    }

    static getPlanningFromSelectedVariantByAppId(
        project: ProjectResponseGetById,
        applicationId: PlanningGetById['applicationId'],
        options?: { withEmptyProducts: boolean }
    ): PlanningGetById | undefined {
        return ProjectUtils.getPlanningsFromSelectedVariant(project, options)?.find(
            planning => planning['applicationId'] === applicationId
        );
    }

    static getPlanningFromSelectedVariantByPlanningId(
        project: ProjectResponseGetById,
        planningId: string,
        options?: { withEmptyProducts: boolean }
    ): PlanningGetById | undefined {
        return ProjectUtils.getPlanningsFromSelectedVariant(project, options)?.find(planning => planning.id === planningId);
    }

    static hasPlanningsWithProductsAmongAllVariants(project: ProjectResponseGetById): boolean {
        const plannings = ProjectUtils.getPlanningsWithProductsFromVariants(project);
        return plannings ? plannings.length > 0 : false;
    }

    static isUnopenedByPartner(project: ProjectsListProjectGetRecordsList | ProjectResponseGetById): boolean {
        return !!(
            project.tags?.some(tag => tag === ProjectTag.UnopenedByPartner) &&
            ((project as ProjectsListProjectGetRecordsList).partnerNumber ||
                (project as ProjectResponseGetById)?.building?.installer?.partnerNumber)
        );
    }

    static isExternalProject(project: ProjectsListProjectGetRecordsList | ProjectResponseGetById): boolean {
        return !!project.sourceApplicationId && project.projectStatus === ProjectStatus.New;
    }
}
